<template>
    <div
        ref="customSelect"
        :class="[
            'custom-select',
            isExpanded ? 'expanded' : '',
            isReverse ? 'reversed' : '',
            isValid ? 'valid' : '',
            hasError ? 'has-error' : '',
        ]"
        :style="{
            top: isExpanded
                ? isReverse
                    ? 'unset'
                    : '-' + listHeight + 'px'
                : '0',
            bottom: isExpanded && isReverse ? '-' + listHeight + 'px' : 'unset',
            border: isExpanded ? '1px solid #192241' : '',
            boxShadow: isExpanded ? '0 3px 6px 0 rgba(0, 0, 0, 0.16)' : '',
        }"
        @click="toggleSelect"
        @focusout="handleFocusOut"
    >
        <select ref="select" class="visually-hidden" @focus="toggleSelect" />
        <span
            class="custom-select__label"
            :class="[selectedDisplayValue !== placeholder ? 'not-empty' : '']"
            :style="{ opacity: isExpanded ? 1 : 0 }"
        >
            {{ label }}
        </span>
        <div ref="selectDisplay" class="custom-select__input">
            <span
                class="custom-select__input-value"
                :class="[
                    selectedDisplayValue !== placeholder ? 'isSelected' : '',
                ]"
            >
                <span
                    v-if="flags && id"
                    :class="`country-flag fi fi-${id.toLowerCase()}`"
                />
                {{ truncatedValue }}
            </span>
            <span
                class="custom-select__input-arrow"
                :style="{ transform: isExpanded ? 'rotate(-135deg)' : '' }"
            />
        </div>
        <ItemList
            ref="itemListRef"
            class="custom-select__item-list fs-11"
            :is-default-open="isOpen"
            :item-list="itemList"
            :show-flags="flags"
            @select-option="selectOption"
        />
        <Error :name="name" />
    </div>
</template>

<script lang="ts">
import {
    defineComponent,
    ref,
    computed,
    onBeforeMount,
    onMounted,
    onBeforeUnmount,
    watch,
    nextTick,
    toRefs,
    PropType,
} from 'vue';
import { useStore } from 'vuex';
import Error from '@/components/basic/Error.vue';
import ItemList from '@/components/basic/ItemList.vue';
import { UtilityHelper } from '@/helpers/UtilityHelper';
import { DELETE_ERROR, SET_SELECT_STATE } from '@/constants/mutation-types';
import type { FieldInterface } from '@/types';

const MAX_SELECT_LIST_HEIGHT = window.innerHeight < 500 ? 140 : 250;

export default defineComponent({
    name: 'CustomSelect',
    components: { Error, ItemList },
    props: {
        itemList: {
            type: Array as PropType<FieldInterface[]>,
            required: true,
        },
        placeholder: { type: String, default: '' },
        label: { type: String, default: '' },
        name: { type: String, default: '' },
        modelValue: { type: String, default: '' },
        handlesDataPreload: { type: Boolean, default: true },
        isValidated: { type: Boolean, default: true },
        defaultValue: { type: String, default: null },
        flags: { type: Boolean, default: false },
    },
    emits: ['update:modelValue', 'focus', 'blur'],
    setup(props, { emit }) {
        const store = useStore();

        const { placeholder, label } = toRefs(props);

        const customSelect = ref<HTMLElement | null>(null);
        const select = ref<HTMLElement | null>(null);
        const selectDisplay = ref<HTMLElement | null>(null);
        const itemListRef = ref<any>(null);
        const currentValue = ref<string | null>(props.modelValue ?? null);

        const id = ref('');
        const isExpanded = ref(true);
        const model = ref(props.placeholder || '');
        const isReverse = ref(false);
        const listHeight = ref(0);
        const isOpen = ref(false);
        const isValid = ref(false);
        const selectedDisplayValue = ref(placeholder.value || '');

        const isSelectOpen = computed(() => store.state.isSelectOpen);
        const formDataStore = computed(() => store.state.formData);
        const hasError = computed(() => store.state.errors.get(props.name));
        const isValidFieldsChanged = computed(
            () => store.state.isValidFieldsChanged
        );

        function getBoundingRect(element: any): DOMRect | null {
            const domElement = element && element.$el ? element.$el : element;
            if (
                domElement &&
                typeof domElement.getBoundingClientRect === 'function'
            ) {
                return domElement.getBoundingClientRect();
            }
            return null;
        }

        onBeforeMount(() => {
            const formData = formDataStore.value as Record<string, unknown>;
            for (let i = 0; i < props.itemList.length; i++) {
                if (props.itemList[i].key === formData[props.name]) {
                    const obj = props.itemList[i];
                    props.itemList.splice(i, 1);
                    props.itemList.unshift(obj);
                    break;
                }
            }
        });

        onMounted(() => {
            if (itemListRef.value) {
                const rect = getBoundingRect(itemListRef.value);
                listHeight.value = rect ? rect.height : 0;
            }
            isExpanded.value = false;
            document.addEventListener('click', closeSelectOnOutsideCLick);

            if (props.modelValue && props.modelValue.length > 0) {
                setValue(props.modelValue);
            }
            if (props.defaultValue) {
                setValue(props.defaultValue);
            }
            if (props.handlesDataPreload) {
                loadData();
            }

            if (
                props.isValidated &&
                props.modelValue &&
                !store.state.validFields.has(props.name as string)
            ) {
                store.dispatch('validate', {
                    name: props.name,
                    value: props.modelValue,
                });
            }
        });

        onBeforeUnmount(() => {
            document.removeEventListener('click', closeSelectOnOutsideCLick);
        });

        function closeSelectOnOutsideCLick(evt: MouseEvent): void {
            const target = evt.target as HTMLElement;
            if (customSelect.value && !customSelect.value.contains(target)) {
                close();
            }
        }

        function handleFocusOut(evt: FocusEvent): void {
            const nextTarget = evt.relatedTarget as Node | null;

            if (
                customSelect.value &&
                !customSelect.value.contains(nextTarget)
            ) {
                close();
            }
        }

        function loadData(): void {
            const formData = formDataStore.value as Record<string, unknown>;
            if (
                Object.prototype.hasOwnProperty.call(formData, props.name) &&
                formData[props.name] !== null
            ) {
                props.itemList.some((item: any) => {
                    if (item.key == formData[props.name]) {
                        setValue(item.key.toString());
                        return true;
                    }
                    return false;
                });
            }
        }

        async function setValue(newId: string): Promise<void> {
            await waitForItemList();

            props.itemList.some((item: any) => {
                if (item.key == newId) {
                    selectedDisplayValue.value = item.value;
                    return true;
                }
                return false;
            });

            if (currentValue.value === newId) {
                return;
            }
            currentValue.value = newId;

            if (props.isValidated) {
                store.dispatch('validate', {
                    name: props.name,
                    value: newId,
                });
            }

            emit('update:modelValue', newId);
        }

        function selectOption(item: any): void {
            setValue(item.key.toString());
            close();
        }

        function toggleSelect(): void {
            const refEl = customSelect.value;
            if (!refEl) return;

            const itemListElement = itemListRef.value
                ? itemListRef.value.$el || itemListRef.value
                : null;
            if (!itemListElement) return;

            if (!willFitOnScreen()) {
                refEl.insertBefore(itemListElement, refEl.children[0]);
            } else {
                refEl.appendChild(itemListElement);
            }

            if (isExpanded.value) {
                close();
            } else {
                open();
            }
        }

        async function open(): Promise<void> {
            store.commit(SET_SELECT_STATE, false);
            await nextTick();
            isExpanded.value = true;
            store.commit(SET_SELECT_STATE, isExpanded.value);
            emit('focus');
            await UtilityHelper.sleep(5);
            if (itemListRef.value) {
                const rect = getBoundingRect(itemListRef.value);
                listHeight.value = rect ? rect.height : 0;
            }
            isOpen.value = true;
        }

        async function close(): Promise<void> {
            isOpen.value = false;
            isExpanded.value = false;
            emit('blur');
        }

        function willFitOnScreen(): boolean {
            const boundaries = getBoundingRect(customSelect.value);
            const bodyBoundaries = document.body.getBoundingClientRect();
            if (boundaries) {
                isReverse.value =
                    boundaries.bottom + MAX_SELECT_LIST_HEIGHT >=
                    bodyBoundaries.bottom;
                return !isReverse.value;
            }
            return true;
        }

        function waitForItemList(): Promise<void> {
            return new Promise((resolve) => {
                const interval = setInterval(() => {
                    if (
                        Array.isArray(props.itemList) &&
                        props.itemList.length
                    ) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 50);
            });
        }
        watch(placeholder, (next, prev) => {
            if (
                !props.modelValue ||
                selectedDisplayValue.value === (prev ?? '')
            ) {
                selectedDisplayValue.value = next ?? '';
            }
        });

        watch(
            () => isSelectOpen.value,
            (newValue) => {
                if (!newValue) {
                    isExpanded.value = false;
                }
            }
        );

        watch(
            () => isExpanded.value,
            () => {
                store.commit(DELETE_ERROR, props.name);
            }
        );

        watch(
            () => props.modelValue,
            () => {
                if (model.value.length >= 29) {
                    const maxLengthString = model.value.slice(0, 29);
                    const lastSpaceIndex = maxLengthString.lastIndexOf(' ');
                    let result = model.value;
                    if (lastSpaceIndex > -1) {
                        result =
                            maxLengthString.slice(0, lastSpaceIndex) + '...';
                    } else {
                        result =
                            maxLengthString.slice(
                                0,
                                maxLengthString.length - 3
                            ) + '...';
                    }
                    model.value = result;
                }
            }
        );

        const truncatedValue = computed(() => {
            const value = selectedDisplayValue.value;
            if (value.length >= 29) {
                const maxLengthString = value.slice(0, 29);
                const lastSpaceIndex = maxLengthString.lastIndexOf(' ');
                if (lastSpaceIndex > -1) {
                    return maxLengthString.slice(0, lastSpaceIndex) + '...';
                } else {
                    return (
                        maxLengthString.slice(0, maxLengthString.length - 3) +
                        '...'
                    );
                }
            }
            return value;
        });

        watch(
            () => props.modelValue,
            (newValue) => {
                if (newValue && newValue !== currentValue.value) {
                    setValue(newValue);
                }
            }
        );

        watch(
            () => isValidFieldsChanged.value,
            () => {
                isValid.value = store.state.validFields.has(props.name);
            }
        );

        return {
            customSelect,
            select,
            selectDisplay,
            itemListRef,
            isExpanded,
            isReverse,
            listHeight,
            isOpen,
            isValid,
            model,
            id,
            hasError,
            selectedDisplayValue,
            truncatedValue,
            toggleSelect,
            selectOption,
            loadData,
            setValue,
            open,
            close,
            willFitOnScreen,
            waitForItemList,
            closeSelectOnOutsideCLick,
            handleFocusOut,
            placeholder,
            label,
        };
    },
});
</script>
